import type { Agent, Effect, Player, Round, Weapon } from "../api/types";
import { AbilityLookup, EFFECT_STYLE, effectsAt } from "../lib/abilities";
import { snapshot, type RoundIndex, type Snapshot } from "../lib/roundState";
import { isAttacker, sideColor } from "../lib/view";
import { useCoarseTime, usePlayback } from "../player/store";

interface Props {
  players: Player[];
  agents: Map<string, Agent>;
  weapons: Weapon[];
  lookup: AbilityLookup;
  round: Round;
  index: RoundIndex;
  effects: Effect[];
}

/** The order abilities sit in, as the game lays them out on the keyboard. */
const SLOTS = ["Grenade", "Ability1", "Ability2"] as const;
const ULTIMATE = "Ultimate";

const BASE_HEALTH = 100;

/** Both teams with their live state: agent, loadout, health, armour, abilities
 *  and how close their ultimate is. */
export function PlayerPanel({ players, agents, weapons, lookup, round, index, effects }: Props) {
  const timeMs = useCoarseTime(100);
  const focusPawn = usePlayback((s) => s.focusPawn);
  const focus = usePlayback((s) => s.focus);

  const byName = new Map(weapons.map((w) => [w.displayName, w]));
  const attackers = players.filter((p) => isAttacker(p, round));
  const defenders = players.filter((p) => !isAttacker(p, round));

  const renderTeam = (team: Player[], label: string) => (
    <div>
      <h3 className="px-3 pb-1 pt-3 text-[11px] uppercase tracking-wide opacity-50">{label}</h3>
      {team.map((player) => {
        const state = snapshot(index, player.pawn, timeMs);
        const active = effectsAt(effects, player.pawn, timeMs, round.endMs);
        const agent = agents.get(player.codename);
        const color = sideColor(player, round);
        const focused = focusPawn === player.pawn;
        const faded = focusPawn !== null && !focused;
        const guns = heldGuns(state, byName);

        return (
          <button
            key={player.pawn}
            onClick={() => focus(player.pawn)}
            className={`flex w-full gap-2 px-3 py-2 text-left transition ${
              focused ? "bg-white/10" : "hover:bg-white/5"
            } ${faded ? "opacity-40" : ""} ${state.alive ? "" : "grayscale"}`}
          >
            {agent?.icon ? (
              <img
                src={agent.icon}
                alt=""
                className="h-9 w-9 shrink-0 rounded"
                style={{ outline: `2px solid ${color}` }}
              />
            ) : (
              <span
                className="grid h-9 w-9 shrink-0 place-items-center rounded text-xs font-bold"
                style={{ background: `${color}33`, color }}
              >
                {(agent?.displayName ?? player.codename).slice(0, 2)}
              </span>
            )}

            <span className="min-w-0 flex-1">
              <span className="flex items-baseline justify-between gap-2">
                <span className="truncate text-sm">
                  {agent?.displayName ?? player.codename}
                </span>
                <span className="shrink-0 font-mono text-[11px] opacity-60">
                  {player.kills}/{player.deaths}
                </span>
              </span>

              <LifeBar state={state} />

              <span className="mt-1 flex items-center gap-2 text-[11px]">
                <span className="font-mono">
                  {state.alive ? <Life state={state} /> : <span className="opacity-60">mort</span>}
                </span>
                {state.credits !== null && (
                  <span className="ml-auto shrink-0 font-mono opacity-50">
                    {state.credits.toLocaleString("fr-FR")} €
                  </span>
                )}
              </span>

              <span className="mt-1.5 flex items-center gap-1.5">
                {guns.map((gun) => (
                  <span
                    key={gun.displayName}
                    title={gun.displayName}
                    className={`flex h-5 items-center rounded bg-white/5 px-1 ${
                      state.weapon === gun.displayName ? "ring-1 ring-white/40" : ""
                    }`}
                  >
                    {gun.icon ? (
                      <img src={gun.icon} alt="" className="h-3.5 w-10 object-contain" />
                    ) : (
                      <span className="text-[10px]">{gun.displayName}</span>
                    )}
                  </span>
                ))}
                {guns.length === 0 && <span className="text-[11px] opacity-40">—</span>}
              </span>

              <span className="mt-1.5 flex items-center gap-1">
                {SLOTS.map((slot) => (
                  <AbilityChip
                    key={slot}
                    icon={lookup.ability(player.codename, slot)?.icon}
                    name={lookup.ability(player.codename, slot)?.displayName ?? slot}
                    charges={state.charges[slot] ?? 0}
                    color={color}
                  />
                ))}
                <UltimateChip
                  icon={lookup.ability(player.codename, ULTIMATE)?.icon}
                  name={lookup.ability(player.codename, ULTIMATE)?.displayName ?? ULTIMATE}
                  points={state.ultimate}
                  cost={lookup.ultimateCost(player.codename)}
                  color={color}
                />
              </span>

              {active.length > 0 && (
                <span className="mt-1.5 flex flex-wrap gap-1">
                  {active.map((effect, i) => {
                    const style = EFFECT_STYLE[effect.category];
                    return (
                      <span
                        key={`${effect.category}-${i}`}
                        // The container name is worth showing when it is one;
                        // the ultimate's "effect" is a replicated flag.
                        title={effect.class.startsWith("FXC_") ? effect.class : style.label}
                        className="rounded px-1 text-[10px] leading-4"
                        style={{ background: `${style.color}26`, color: style.color }}
                      >
                        {style.label}
                      </span>
                    );
                  })}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="overflow-y-auto">
      {renderTeam(attackers, "Attaque")}
      {renderTeam(defenders, "Défense")}
    </div>
  );
}

/**
 * Health and armour as one bar scaled to what the player can actually take.
 * The health figure is exact; the armour is the one they bought, shown until it
 * breaks, because the game never replicates a running value for it.
 */
function LifeBar({ state }: { state: Snapshot }) {
  const total = BASE_HEALTH + state.armour;
  return (
    <span className="mt-1 flex h-1.5 gap-px overflow-hidden rounded-full bg-white/10">
      <span
        className="h-full rounded-l-full"
        style={{
          width: `${(Math.max(0, state.health) / total) * 100}%`,
          background: state.alive ? "#5ddc7a" : "#5a3a3a",
        }}
      />
      {state.armour > 0 && state.alive && (
        <span
          className="h-full"
          style={{ width: `${(state.armour / total) * 100}%`, background: "#7ad4ff" }}
        />
      )}
    </span>
  );
}

function Life({ state }: { state: Snapshot }) {
  const health = Math.round(state.health);
  if (state.armour === 0) return <span>{health} PV</span>;
  return (
    <span>
      {health + state.armour} PV{" "}
      <span className="opacity-50">
        ({health} + <span style={{ color: "#7ad4ff" }}>{state.armour}</span>)
      </span>
    </span>
  );
}

/** One basic ability: its icon, dimmed when none is left, with a pip per charge. */
function AbilityChip({
  icon,
  name,
  charges,
  color,
}: {
  icon?: string;
  name: string;
  charges: number;
  color: string;
}) {
  const has = charges > 0;
  return (
    <span
      title={`${name} — ${charges} charge${charges > 1 ? "s" : ""}`}
      className="relative grid h-6 w-6 place-items-center rounded"
      style={{
        background: has ? `${color}26` : "rgba(255,255,255,0.04)",
        opacity: has ? 1 : 0.3,
      }}
    >
      {icon ? (
        <img src={icon} alt="" className="h-4 w-4 object-contain" />
      ) : (
        <span className="text-[9px]">{name.slice(0, 2)}</span>
      )}
      {charges > 1 && (
        <span className="absolute -bottom-0.5 -right-0.5 rounded bg-black/70 px-[3px] text-[9px] leading-3">
          {charges}
        </span>
      )}
    </span>
  );
}

/** The ultimate: points out of what it costs, lit once it is ready. */
function UltimateChip({
  icon,
  name,
  points,
  cost,
  color,
}: {
  icon?: string;
  name: string;
  points: number;
  cost: number;
  color: string;
}) {
  const ready = cost > 0 && points >= cost;
  return (
    <span
      title={`${name} — ${points} / ${cost || "?"} points`}
      className="ml-1 flex h-6 items-center gap-1 rounded px-1"
      style={{
        background: ready ? `${color}40` : "rgba(255,255,255,0.04)",
        boxShadow: ready ? `inset 0 0 0 1px ${color}` : undefined,
      }}
    >
      {icon ? (
        <img
          src={icon}
          alt=""
          className="h-4 w-4 object-contain"
          style={{ opacity: ready ? 1 : 0.45 }}
        />
      ) : (
        <span className="text-[9px]">X</span>
      )}
      <span className="font-mono text-[10px]" style={{ opacity: ready ? 1 : 0.55 }}>
        {ready ? "prêt" : `${points}/${cost || "?"}`}
      </span>
    </span>
  );
}

/**
 * The guns the player has held this round, one primary and one sidearm. Nothing
 * replicates an inventory, but what somebody has had in their hands is the same
 * information for this purpose.
 */
function heldGuns(state: Snapshot, byName: Map<string, Weapon>): Weapon[] {
  let primary: Weapon | undefined;
  let sidearm: Weapon | undefined;
  for (const name of state.weapons) {
    const gun = byName.get(name);
    if (!gun || gun.category === "Melee") continue;
    if (gun.category === "Pistols") sidearm = gun;
    else primary = gun;
  }
  return [primary, sidearm].filter((w): w is Weapon => !!w);
}
